'use client';

// ═══════════════════════════════════════════════════════════════════════
// PendingReviewPanel — side-pinned, non-modal review surface.
//
// Replaces the previous full-screen modal. Renders as a 400-px right-
// pinned column on desktop (full-screen sheet on mobile). Critically
// NOT a modal: there's no backdrop, so the user can still click around
// the page underneath while the panel is open. They can navigate to
// /stocks/<ticker> from any candidate's link and the panel persists
// because it's globally mounted in app/layout.tsx.
//
// Three visible states governed by the parent layer:
//   • hidden    — not rendered
//   • collapsed — thin 36-px strip on the right edge with the queue
//                 count rotated vertically. Click anywhere on the strip
//                 to expand. Mobile uses a floating circular pill in
//                 the bottom-right.
//   • expanded  — full content. Left column lists every candidate
//                 across every pending review the user owns. Right
//                 column (or below, on mobile) shows the selected
//                 candidate's detail + action buttons.
//
// Actions act on the selected candidate (or the whole review if the
// user clicks Approve-all). The API already supports approving a
// subset via `{ tickers: [...] }`, so multi-candidate fills work
// without server changes.
// ═══════════════════════════════════════════════════════════════════════

import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import {
  X, ChevronRight, Minimize2, Loader2, Check, Ban, Clock, Lock,
  AlertTriangle, ExternalLink, Bell,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type {
  PendingReview,
  PendingReviewApproveResult,
  PendingReviewCandidate,
  PendingReviewRejectResolution,
  PendingReviewSkipResult,
} from '@/lib/api-client';

export type PanelMode = 'hidden' | 'collapsed' | 'expanded';

interface PendingReviewPanelProps {
  /** Full queue across the user's portfolios. The panel manages its
   *  own selection / detail state, but reads the queue from the parent. */
  queue: PendingReview[];
  mode: PanelMode;
  onModeChange: (mode: PanelMode) => void;
  onApprove: (id: string, input: { tickers: string[]; reason?: string }) => Promise<PendingReviewApproveResult>;
  onReject: (id: string, input: { resolution: PendingReviewRejectResolution; tickers?: string[]; snoozeMinutes?: number; reason?: string }) => Promise<PendingReview | PendingReviewSkipResult>;
}

interface PanelItem {
  review: PendingReview;
  candidate: PendingReviewCandidate;
  /** Synthetic stable key: `<reviewId>:<ticker>`. */
  key: string;
}

function flatten(queue: PendingReview[]): PanelItem[] {
  const items: PanelItem[] = [];
  for (const review of queue) {
    const ranked = review.candidates.slice().sort((a, b) => a.score - b.score);
    for (const cand of ranked) {
      items.push({ review, candidate: cand, key: `${review.id}:${cand.ticker.toUpperCase()}` });
    }
  }
  return items;
}

function minutesUntilEOD(): number {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
  const parts = fmt.formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const sampleEt = new Date(`${get('year')}-${get('month')}-${get('day')}T16:00:00Z`);
  const sampleHour = parseInt(
    fmt.formatToParts(sampleEt).find((p) => p.type === 'hour')?.value ?? '0',
    10,
  );
  const offsetHours = 16 - sampleHour;
  const eodUtcMin = Math.floor(Date.UTC(
    parseInt(get('year'), 10),
    parseInt(get('month'), 10) - 1,
    parseInt(get('day'), 10),
    16, 0, 0,
  ) / 60_000) + offsetHours * 60;
  return Math.max(0, eodUtcMin - Math.floor(now.getTime() / 60_000));
}

export default function PendingReviewPanel({
  queue, mode, onModeChange, onApprove, onReject,
}: PendingReviewPanelProps) {
  const items = useMemo(() => flatten(queue), [queue]);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  /** Multi-select batch. The user can check multiple candidates to
   *  Approve / Skip in one click. Defaults to just the focused item
   *  (selectedKey) — same as the prior single-select UX. Independent
   *  of the focused detail view: clicking a row's body changes
   *  selection (detail); clicking the checkbox toggles batch
   *  membership. */
  const [checkedKeys, setCheckedKeys] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState<null | 'approve' | 'skip' | 'snooze' | 'manual_hold'>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Transient "Filled NVDA × 78 @ $1245" toast that fades after a few
  // seconds. Doesn't block the panel; the user keeps reviewing.
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // Auto-select + auto-check first item when:
  //   • no selection AND we have items
  //   • current selection drops out of the queue (approved/rejected/expired)
  useEffect(() => {
    if (items.length === 0) {
      setSelectedKey(null);
      setCheckedKeys(new Set());
      return;
    }
    if (!selectedKey || !items.find((i) => i.key === selectedKey)) {
      setSelectedKey(items[0].key);
      setReason('');
      setError(null);
      // Reset batch to just the new focused candidate. Prevents
      // stale-key build-up across review cycles.
      setCheckedKeys(new Set([items[0].key]));
    }
  }, [items, selectedKey]);

  // Drop any checked keys that no longer exist in the queue (acted
  // upon, expired, or otherwise vanished). Cheap O(n) over a small
  // set; runs on every poll cycle.
  useEffect(() => {
    setCheckedKeys((prev) => {
      const live = new Set(items.map((i) => i.key));
      const next = new Set<string>();
      let changed = false;
      for (const k of prev) {
        if (live.has(k)) next.add(k);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [items]);

  // Clear inputs when switching candidates.
  useEffect(() => {
    setError(null);
    setReason('');
  }, [selectedKey]);

  // Auto-hide the inline toast after 4 s so it doesn't stack.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const toggleChecked = (key: string) => {
    setCheckedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Queue went empty → auto-hide.
  useEffect(() => {
    if (items.length === 0 && mode !== 'hidden') {
      onModeChange('hidden');
    }
  }, [items.length, mode, onModeChange]);

  if (mode === 'hidden' || items.length === 0) return null;

  const selected = items.find((i) => i.key === selectedKey) ?? items[0];

  // ── Collapsed: thin right-edge strip / mobile floating pill ─────────────
  if (mode === 'collapsed') {
    return (
      <>
        {/* Desktop: vertical strip on right edge */}
        <button
          type="button"
          onClick={() => onModeChange('expanded')}
          aria-label="Open pending reviews panel"
          className="hidden sm:flex fixed right-0 top-1/2 -translate-y-1/2 z-[110] flex-col items-center gap-2 bg-card-bg border border-default border-r-0 rounded-l-lg px-1.5 py-3 shadow-lg hover:bg-surface transition-colors"
          style={{ writingMode: 'vertical-rl' }}
        >
          <Bell size={14} className="text-fuchsia-400 -rotate-90" />
          <span className="text-[11px] font-mono uppercase tracking-wider text-white">
            {items.length} review{items.length === 1 ? '' : 's'}
          </span>
        </button>
        {/* Mobile: floating circular pill in bottom-right */}
        <button
          type="button"
          onClick={() => onModeChange('expanded')}
          aria-label="Open pending reviews panel"
          className="sm:hidden fixed right-4 bottom-4 z-[110] inline-flex items-center gap-1.5 px-3 py-2 bg-fuchsia-500/15 border border-fuchsia-500/40 rounded-full text-fuchsia-200 shadow-lg"
        >
          <Bell size={14} />
          <span className="text-xs font-mono font-bold">{items.length}</span>
        </button>
      </>
    );
  }

  // ── Expanded ────────────────────────────────────────────────────────────
  // Higher z-index than the side-slide (z-[100]) so a brief race during the
  // hidden→expanded transition can't leave the slide painted on top of the
  // panel. Explicit `sm:left-auto` instead of relying on `sm:inset-auto`
  // ordering — Tailwind's CSS source order isn't guaranteed across builds
  // and `inset-auto` after `right-0` can cancel the pin in some toolchains.
  return (
    <aside
      role="complementary"
      aria-label="Pending auto-trade reviews"
      className={cn(
        'fixed z-[110] bg-card-bg shadow-2xl flex flex-col',
        // Mobile: full-screen sheet.
        'inset-0',
        // Desktop: 400 px right-pinned column, full viewport height.
        'sm:left-auto sm:right-0 sm:top-0 sm:bottom-0 sm:w-[400px]',
        'sm:border-l sm:border-default',
      )}
    >
      <PanelHeader
        count={items.length}
        selected={selected}
        onMinimize={() => onModeChange('collapsed')}
        onClose={() => onModeChange('hidden')}
        disabled={!!submitting}
      />

      {/* Slot accounting strip — only for buy reviews. Surfaces live
          slot budget so the user can size their multi-select. */}
      {selected.review.action === 'buy' && selected.review.maxConcurrent != null && (
        <div className="px-4 py-2 border-b border-default bg-surface/30 text-[11px] flex items-center justify-between gap-2">
          <span className="text-fg-subtle">
            Slots:{' '}
            <span className={cn(
              'font-mono font-medium',
              (selected.review.slotsAvailable ?? 0) > 0 ? 'text-emerald-300' : 'text-rose-300',
            )}>
              {selected.review.slotsAvailable ?? '—'}
            </span>
            <span className="text-fg-subtle"> of {selected.review.maxConcurrent} available</span>
          </span>
          <span className="text-fg-subtle">
            Selected: <span className="text-white font-mono font-medium">{checkedKeys.size}</span>
          </span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {items.length > 1 && (
          <CandidateList
            items={items}
            selectedKey={selected.key}
            checkedKeys={checkedKeys}
            onSelect={(k) => setSelectedKey(k)}
            onToggleChecked={toggleChecked}
          />
        )}
        <CandidateDetail
          item={selected}
          reason={reason}
          onReasonChange={setReason}
          submitting={submitting}
          error={error}
        />
      </div>

      <PanelActions
        item={selected}
        items={items}
        checkedKeys={checkedKeys}
        submitting={submitting}
        toast={toast}
        onApprove={async () => {
          const targets = items.filter((i) => checkedKeys.has(i.key) && i.review.id === selected.review.id);
          if (targets.length === 0) {
            setError('Check at least one candidate to approve.');
            return;
          }
          const tickers = targets.map((t) => t.candidate.ticker);
          setSubmitting('approve'); setError(null);
          try {
            const result = await onApprove(selected.review.id, {
              tickers,
              reason: reason.trim() || undefined,
            });
            // Build a compact toast summarising the batch outcome.
            if (result.executed.length === 1 && result.skipped.length === 0) {
              const e = result.executed[0];
              setToast({ kind: 'ok', text: `Filled ${e.ticker} × ${e.shares} @ $${e.price.toFixed(2)}` });
            } else if (result.executed.length > 1 && result.skipped.length === 0) {
              const tot = result.executed.reduce((s, e) => s + e.shares * e.price, 0);
              setToast({ kind: 'ok', text: `Filled ${result.executed.length} candidates · $${tot.toFixed(0)} deployed` });
            } else if (result.skipped.length > 0) {
              const sk = result.skipped[0];
              setToast({
                kind: result.executed.length > 0 ? 'ok' : 'err',
                text: `Filled ${result.executed.length} · skipped ${result.skipped.length} (${sk.reason})`,
              });
            }
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
          } finally { setSubmitting(null); }
        }}
        onReject={async (resolution, snoozeMinutes) => {
          const id = resolution === 'snooze' ? 'snooze'
            : resolution === 'manual_hold' ? 'manual_hold' : 'skip';
          setSubmitting(id); setError(null);
          try {
            // Skip acts on the checked batch (drop each from the row's
            // candidates). Snooze + manual_hold are whole-row — the
            // API ignores tickers there.
            let tickers: string[] | undefined;
            if (resolution === 'skip') {
              const targets = items.filter((i) => checkedKeys.has(i.key) && i.review.id === selected.review.id);
              if (targets.length === 0) {
                setError('Check at least one candidate to skip.');
                return;
              }
              tickers = targets.map((t) => t.candidate.ticker);
            }
            await onReject(selected.review.id, {
              resolution, tickers, snoozeMinutes,
              reason: reason.trim() || undefined,
            });
            if (resolution === 'skip' && tickers) {
              setToast({
                kind: 'ok',
                text: tickers.length === 1 ? `Skipped ${tickers[0]}` : `Skipped ${tickers.length} candidates`,
              });
            }
          } catch (err) {
            setError(err instanceof Error ? err.message : String(err));
          } finally { setSubmitting(null); }
        }}
      />
    </aside>
  );
}

// ── Header ────────────────────────────────────────────────────────────────

function PanelHeader({
  count, selected, onMinimize, onClose, disabled,
}: {
  count: number;
  selected: PanelItem;
  onMinimize: () => void;
  onClose: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-default">
      <div className="min-w-0">
        <div className="flex items-center gap-1.5">
          <Bell size={12} className="text-fuchsia-400 shrink-0" />
          <span className="text-[11px] uppercase tracking-wide text-fg-subtle truncate">
            {selected.review.portfolioName ?? 'Auto-trade review'}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-fuchsia-500/15 border border-fuchsia-500/30 text-fuchsia-300 font-mono shrink-0">
            {count}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onMinimize}
          disabled={disabled}
          aria-label="Minimize panel"
          className="text-fg-subtle hover:text-white p-1.5 -m-1 disabled:opacity-40 transition-colors"
        >
          <Minimize2 size={14} />
        </button>
        <button
          type="button"
          onClick={onClose}
          disabled={disabled}
          aria-label="Close panel"
          className="text-fg-subtle hover:text-white p-1.5 -m-1 disabled:opacity-40 transition-colors"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}

// ── Candidate list (multi-candidate selector) ─────────────────────────────

function CandidateList({
  items, selectedKey, checkedKeys, onSelect, onToggleChecked,
}: {
  items: PanelItem[];
  selectedKey: string;
  checkedKeys: Set<string>;
  onSelect: (key: string) => void;
  onToggleChecked: (key: string) => void;
}) {
  return (
    <div className="border-b border-default">
      <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wide text-fg-subtle">
        Candidates ({items.length})
        <span className="text-fg-subtle/70 normal-case text-[10px] ml-1">
          · check to include in Approve / Skip
        </span>
      </div>
      <div className="divide-y divide-default/50">
        {items.map((item) => {
          const isSel = item.key === selectedKey;
          const isChecked = checkedKeys.has(item.key);
          const isBuy = item.review.action === 'buy';
          const chg = item.candidate.changePct;
          return (
            <div
              key={item.key}
              className={cn(
                'w-full px-4 py-2 transition-colors flex items-center gap-2 min-h-[44px]',
                isSel ? 'bg-fuchsia-500/10' : 'hover:bg-surface',
              )}
            >
              <span className={cn(
                'shrink-0 w-1 h-7 rounded-r',
                isSel ? 'bg-fuchsia-400' : 'bg-transparent',
              )} />
              <label
                className="shrink-0 inline-flex items-center cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggleChecked(item.key)}
                  aria-label={`Include ${item.candidate.ticker} in batch action`}
                  className="cursor-pointer h-4 w-4 accent-fuchsia-500"
                />
              </label>
              <button
                type="button"
                onClick={() => onSelect(item.key)}
                className="flex-1 min-w-0 flex items-center gap-2 text-left"
              >
                <span className={cn(
                  'text-[9px] font-bold uppercase px-1 py-0.5 rounded border',
                  isBuy
                    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                    : 'bg-rose-500/15 border-rose-500/40 text-rose-300',
                )}>
                  {isBuy ? 'B' : 'S'}
                </span>
                <span className="text-white font-bold text-sm">{item.candidate.ticker}</span>
                <span className="text-fg-subtle text-[11px] truncate">{item.candidate.qualificationReason}</span>
                <span className="ml-auto text-right shrink-0">
                  <span className="text-white font-mono text-xs">${item.candidate.price.toFixed(2)}</span>
                  {isBuy && (
                    <span className={cn(
                      'ml-2 text-[11px] font-mono',
                      chg >= 0 ? 'text-emerald-400' : 'text-rose-400',
                    )}>
                      {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
                    </span>
                  )}
                </span>
                <ChevronRight size={12} className={cn(
                  'shrink-0 transition-transform',
                  isSel ? 'text-fuchsia-400' : 'text-fg-subtle',
                )} />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Detail panel for the selected candidate ───────────────────────────────

function CandidateDetail({
  item, reason, onReasonChange, submitting, error,
}: {
  item: PanelItem;
  reason: string;
  onReasonChange: (v: string) => void;
  submitting: string | null;
  error: string | null;
}) {
  const { review, candidate } = item;
  const isBuy = review.action === 'buy';
  const chg = candidate.changePct;
  const pnlStr = (() => {
    if (!isBuy && candidate.entryPrice && candidate.entryPrice > 0) {
      const pnl = ((candidate.price - candidate.entryPrice) / candidate.entryPrice) * 100;
      return `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}%`;
    }
    return null;
  })();

  return (
    <div className="px-4 py-4 space-y-4">
      <div className="flex items-start gap-3 justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={cn(
              'text-[10px] uppercase font-bold px-1.5 py-0.5 rounded border',
              isBuy
                ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300'
                : 'bg-rose-500/15 border-rose-500/40 text-rose-300',
            )}>
              {isBuy ? 'Buy' : 'Sell'}
            </span>
            <span className="text-white font-bold text-lg">{candidate.ticker}</span>
            <span className="text-white font-mono">${candidate.price.toFixed(2)}</span>
            {isBuy && (
              <span className={cn('font-mono text-sm', chg >= 0 ? 'text-emerald-400' : 'text-rose-400')}>
                {chg >= 0 ? '+' : ''}{chg.toFixed(2)}%
              </span>
            )}
            {pnlStr && (
              <span className={cn('font-mono text-sm', pnlStr.startsWith('+') ? 'text-emerald-400' : 'text-rose-400')}>
                {pnlStr}
              </span>
            )}
          </div>
          <div className="text-[11px] text-fg-subtle mt-1">
            {isBuy
              ? `${candidate.qualificationReason} · ${candidate.rvol > 0 ? candidate.rvol.toFixed(1) + '× RVOL' : ''}`
              : `Exit: ${candidate.exitReason ?? 'session_close'}`}
          </div>
        </div>
        <Link
          href={`/stocks/${encodeURIComponent(candidate.ticker)}`}
          className="shrink-0 inline-flex items-center gap-1 text-[11px] text-sky-300 hover:text-sky-200 transition-colors"
          title="Open the stock page — panel stays open"
        >
          <ExternalLink size={12} />
          Chart
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {isBuy ? (
          <>
            <Stat label="RVOL" value={candidate.rvol > 0 ? `${candidate.rvol.toFixed(2)}×` : '—'} />
            <Stat label="$ Vol" value={fmtDollar(candidate.dollarVolume)} />
            <Stat label="Severity" value={candidate.severity} />
            <Stat label="Bucket" value={candidate.bucket ?? 'main'} />
          </>
        ) : (
          <>
            <Stat label="Shares" value={candidate.shares ? candidate.shares.toLocaleString() : '—'} />
            <Stat label="Entry" value={candidate.entryPrice ? `$${candidate.entryPrice.toFixed(2)}` : '—'} />
            <Stat label="Current" value={`$${candidate.price.toFixed(2)}`} />
            <Stat label="Peak %" value={candidate.peakUnrealizedPct != null ? `${candidate.peakUnrealizedPct.toFixed(1)}%` : '—'} />
          </>
        )}
      </div>

      <div>
        <label className="block text-[11px] text-fg-muted mb-1">Reason / notes (optional)</label>
        <textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          rows={2}
          maxLength={500}
          disabled={!!submitting}
          placeholder={isBuy ? 'e.g. "news catalyst"' : 'e.g. "let it run"'}
          className="w-full bg-card-bg border border-default focus:border-sky-500 rounded-md px-3 py-2 text-sm text-white focus:outline-none transition-colors disabled:opacity-50 resize-none"
        />
      </div>

      {error && (
        <div className="bg-rose-500/10 border border-rose-500/40 rounded-md p-2 text-[11px] text-rose-300 flex items-start gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-surface border border-default rounded-md px-2 py-1.5">
      <div className="text-[10px] text-fg-subtle uppercase tracking-wide">{label}</div>
      <div className="text-white font-mono text-sm mt-0.5 truncate">{value}</div>
    </div>
  );
}

function fmtDollar(v: number): string {
  if (!Number.isFinite(v) || v <= 0) return '—';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

// ── Footer action row ─────────────────────────────────────────────────────

function PanelActions({
  item, items, checkedKeys, submitting, toast, onApprove, onReject,
}: {
  item: PanelItem;
  items: PanelItem[];
  checkedKeys: Set<string>;
  submitting: string | null;
  toast: { kind: 'ok' | 'err'; text: string } | null;
  onApprove: () => Promise<void>;
  onReject: (resolution: PendingReviewRejectResolution, snoozeMinutes?: number) => Promise<void>;
}) {
  const isBuy = item.review.action === 'buy';
  const eodMin = useMemo(() => minutesUntilEOD(), []);
  const eodLabel = eodMin <= 0 ? 'EOD (closed)' : eodMin < 60 ? `EOD (${eodMin}m)` : `EOD (${Math.floor(eodMin / 60)}h${eodMin % 60 ? ' ' + (eodMin % 60) + 'm' : ''})`;
  // Batch count for the current review's checked rows. Excludes
  // candidates from OTHER reviews (multi-portfolio queue) — those
  // require their own Approve cycle since each review row is a
  // separate atomic action server-side.
  const batchCount = items.filter((i) => checkedKeys.has(i.key) && i.review.id === item.review.id).length;
  const slotsAvail = item.review.slotsAvailable ?? null;
  const overSlots = isBuy && slotsAvail != null && batchCount > slotsAvail && slotsAvail >= 0;
  const approveLabel = batchCount === 0
    ? (isBuy ? 'Approve' : 'Approve')
    : batchCount === 1 ? 'Approve' : `Approve ${batchCount}`;
  const skipLabel = batchCount <= 1 ? (isBuy ? 'Skip' : 'Dismiss') : `Skip ${batchCount}`;

  return (
    <div className="px-4 py-3 border-t border-default bg-surface/50 space-y-2">
      {toast && (
        <div className={cn(
          'text-[11px] rounded-md px-2 py-1.5 border',
          toast.kind === 'ok'
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
            : 'bg-rose-500/10 border-rose-500/40 text-rose-200',
        )}>
          {toast.text}
        </div>
      )}
      {overSlots && (
        <div className="text-[10px] text-amber-300 -mt-1 mb-1">
          {batchCount} selected exceeds {slotsAvail} available — the over-flow will skip with max_concurrent_hit and stay in the panel.
        </div>
      )}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onApprove}
          disabled={!!submitting || batchCount === 0}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/25 disabled:opacity-40 transition-colors min-h-[40px]"
        >
          {submitting === 'approve' ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          {approveLabel}
        </button>
        <button
          type="button"
          onClick={() => onReject('skip')}
          disabled={!!submitting || batchCount === 0}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40 min-h-[40px]"
        >
          {submitting === 'skip' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
          {skipLabel}
        </button>
      </div>
      {isBuy ? (
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onReject('snooze', 15)}
            disabled={!!submitting}
            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40 min-h-[36px]"
          >
            {submitting === 'snooze' ? <Loader2 size={11} className="animate-spin" /> : <Clock size={11} />}
            15m
          </button>
          <button
            type="button"
            onClick={() => onReject('snooze', 30)}
            disabled={!!submitting}
            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40 min-h-[36px]"
          >
            <Clock size={11} /> 30m
          </button>
          <button
            type="button"
            onClick={() => onReject('snooze', eodMin)}
            disabled={!!submitting || eodMin <= 0}
            title="Snooze the whole portfolio until 16:00 ET"
            className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40 min-h-[36px]"
          >
            <Clock size={11} /> {eodLabel}
          </button>
        </div>
      ) : (
        // Sell-review row mirrors the buy row's snooze chips so the
        // user can briefly pause portfolio sells without committing to
        // the permanent manual-hold flag (e.g. "wait 15m for the bar
        // to close before deciding"). Manual hold stays as the
        // permanent escape hatch on its own row below.
        <>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => onReject('snooze', 15)}
              disabled={!!submitting}
              title="Snooze ALL sells for this portfolio for 15 minutes"
              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40 min-h-[36px]"
            >
              {submitting === 'snooze' ? <Loader2 size={11} className="animate-spin" /> : <Clock size={11} />}
              15m
            </button>
            <button
              type="button"
              onClick={() => onReject('snooze', 30)}
              disabled={!!submitting}
              title="Snooze ALL sells for this portfolio for 30 minutes"
              className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-md bg-card-bg border border-default text-fg-subtle hover:text-white transition-colors disabled:opacity-40 min-h-[36px]"
            >
              <Clock size={11} /> 30m
            </button>
            <button
              type="button"
              onClick={() => onReject('manual_hold')}
              disabled={!!submitting}
              title="Permanently exclude this position from auto-exit"
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded-md bg-amber-500/10 border border-amber-500/40 text-amber-300 hover:bg-amber-500/20 transition-colors disabled:opacity-40 min-h-[36px]"
            >
              {submitting === 'manual_hold' ? <Loader2 size={11} className="animate-spin" /> : <Lock size={11} />}
              Hold
            </button>
          </div>
        </>
      )}
      <div className="text-[10px] text-fg-subtle">
        {isBuy
          ? 'Snooze pauses ALL buys for this portfolio. Snoozing also dismisses every pending buy for it.'
          : 'Snooze pauses ALL sells for this portfolio for the chosen window. Hold permanently excludes the position — toggle off from the holdings page.'}
      </div>
    </div>
  );
}
